'use strict';

/**
 * MonitoringUtils — vanilla wrapper around `@sentry/browser`.
 *
 * AngularJS 1.x has no Sentry-specific SDK, so we use the framework-agnostic browser
 * package directly. Mirrors the API surface of Web2's `@qx/monitoring` (init / setUser
 * / setTags / captureException / captureMessage / addBreadcrumb) so log sites in the
 * AngularJS keycloakProvider read the same as their counterparts in the Angular apps.
 *
 * Init is called from `main.js` BEFORE `angular.bootstrap` so any error during the
 * AngularJS boot sequence is captured.
 *
 * @module monitoring.monitoringUtils
 */

import * as Sentry from '@sentry/browser';

const MonitoringUtils = {
  /**
   * Initialize Sentry. No-op if dsn is empty (local dev / Sentry not configured).
   */
  init: function (options) {
    if (!options || !options.dsn) {
      return;
    }

    Sentry.init({
      dsn: options.dsn,
      environment: options.environment,
      release: options.release,
      // Drop noisy 4xx/5xx HttpErrorResponse-equivalents — backend errors aren't FE bugs.
      // The user's keycloakProvider error log sites already capture what we want.
      ignoreErrors: [
        'http',
        'Server',
        'Network Error',
      ],
    });
  },

  setUser: function (directoryEntryUid) {
    if (!directoryEntryUid) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: directoryEntryUid });
  },

  setTags: function (tags) {
    Sentry.setTags(tags);
  },

  captureException: function (err) {
    Sentry.captureException(err);
  },

  /**
   * @param {string} message
   * @param {{ level?: 'fatal'|'error'|'warning'|'log'|'info'|'debug', tags?: object, extra?: object }} [options]
   */
  captureMessage: function (message, options) {
    options = options || {};
    Sentry.captureMessage(message, {
      level: options.level || 'info',
      tags: options.tags,
      extra: options.extra,
    });
  },

  addBreadcrumb: function (breadcrumb) {
    Sentry.addBreadcrumb(breadcrumb);
  },
};

export { MonitoringUtils };
